import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import type {
  ThemeAspectEvidence,
  ThemeBucket,
  ThemeListItem,
  ThemeTrajectory,
} from '@playerbase/types';
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  type BadgeProps,
} from '@playerbase/ui';

import { BuildMarkerLabel } from '../games/build-marker-label.js';
import { buildActiveAt } from '../games/build-timeline.js';
import { useShell } from '../shell/use-shell.js';
import { Collapsible } from './collapsible.js';
import { DismissPatternConfirmModal } from './dismiss-pattern-confirm-modal.js';
import {
  detachAspectFromTheme,
  dismissTheme,
  fetchThemeDetail,
  mergeThemes,
  restoreTheme,
  type ThemeInterventionError,
} from './insights-data.js';
import { MergeThemePicker } from './merge-theme-picker.js';
import { RemoveMentionConfirmModal } from './remove-mention-confirm-modal.js';
import { RestorePatternConfirmModal } from './restore-pattern-confirm-modal.js';
import { relativeTime } from './format-time.js';

// Human-readable labels and badge tones for each bucket. `none` is
// represented as a neutral "simmering" chip rather than the raw enum so
// the AI vocabulary stays out of the surface.
export function bucketDisplayLabel(bucket: ThemeBucket): string {
  if (bucket === 'critical') return 'Critical';
  if (bucket === 'emerging') return 'Emerging';
  if (bucket === 'standout') return 'Standout';
  if (bucket === 'trending') return 'Trending';
  return 'Simmering';
}

export function bucketBadgeTone(bucket: ThemeBucket): BadgeProps['tone'] {
  if (bucket === 'critical') return 'danger';
  if (bucket === 'emerging') return 'warning';
  if (bucket === 'standout') return 'success';
  if (bucket === 'trending') return 'info';
  return 'neutral';
}

// One pattern (theme) in the Insights list. Collapsed: title + polarity
// dot + channel pills (top right) + mention count + share-of-messages +
// insight body with an inline expand button. Expanded: the same row, plus
// up to 5 top mentions ranked by reactions then recency, each linking
// back into the Feedback feed.
//
// AI vocabulary is intentionally absent from copy ("patterns", "mentions",
// "insight" - never "themes", "model", etc).

export interface PatternCardProps {
  pattern: ThemeListItem;
  totalMessages: number;
  // Studio + game slugs for the active context. MentionRow picks
  // between /feedback?focus=<id> (messages, forms, polls, threads) and
  // /playtests/<playtestId>/sessions/<sessionId> (playtest mentions
  // with both ids present) based on the aspect's source. Polls don't
  // have their own URL shape - they reuse the focus URL, with
  // FeedbackInbox resolving the id against pollGroups.
  studioShortId: string;
  gameShortId: string;
  // Sibling themes used as the candidate pool for the merge picker.
  // Optional so any host that hasn't been wired up yet still renders
  // (the merge action is hidden when the list is missing or empty).
  allThemes?: ThemeListItem[] | undefined;
  // Fired after a successful studio intervention so the parent can
  // refetch the themes list. The realtime hook will eventually bump for
  // the same change, but we don't make the acting tab wait for the echo.
  onChanged?: (() => void) | undefined;
}

// Subtle dot instead of a loud uppercase pill. Reads as status without
// dominating the row's typography.
function polarityDotClass(polarity: ThemeListItem['dominantPolarity']): string {
  if (polarity === 'positive') return 'bg-teal';
  if (polarity === 'negative') return 'bg-danger';
  return 'bg-text-muted';
}

function polarityLabel(polarity: ThemeListItem['dominantPolarity']): string {
  if (polarity === 'positive') return 'Positive pattern';
  if (polarity === 'negative') return 'Negative pattern';
  return 'Neutral pattern';
}

// User-facing label for the model's content_axis tag. Lowercased so the
// chip reads as a category, not a heading. We deliberately don't surface
// the raw enum value so future renames don't leak through the UI.
function contentAxisLabel(axis: ThemeListItem['contentAxis']): string {
  if (axis === 'game') return 'game';
  if (axis === 'infrastructure') return 'infrastructure';
  return 'third party';
}

// Rounds to 1dp with a sensible floor so cards with one or two mentions
// in a 50k-message pool still surface a non-zero number.
function formatPercent(numerator: number, denominator: number): string {
  if (denominator <= 0 || numerator <= 0) return '0%';
  const raw = (numerator / denominator) * 100;
  if (raw < 0.1) return '< 0.1%';
  return `${raw.toFixed(1)}%`;
}

// Visible cap on the channel-pill row. Anything beyond is summarised
// with "+N more" so a wide-ranging pattern doesn't blow out the header;
// the overflow chip exposes the rest on hover.
const MAX_VISIBLE_CHANNEL_PILLS = 2;

// Visible cap on the label inside one pill. Poll questions and playtest
// event headlines can run long ("Do you think tropical island trees should
// auto-respawn, like Divi-Divi trees?") and would otherwise blow out the
// card header. Truncated labels keep the full string available on hover.
const MAX_VISIBLE_PILL_LABEL_CHARS = 32;

function truncatePillLabel(label: string): string {
  if (label.length <= MAX_VISIBLE_PILL_LABEL_CHARS) return label;
  return `${label.slice(0, MAX_VISIBLE_PILL_LABEL_CHARS - 1).trimEnd()}…`;
}

interface ChannelPillsProps {
  channels: string[];
}

function ChannelPills({ channels }: ChannelPillsProps): JSX.Element | null {
  if (channels.length === 0) return null;
  const visible = channels.slice(0, MAX_VISIBLE_CHANNEL_PILLS);
  const hidden = channels.slice(MAX_VISIBLE_CHANNEL_PILLS);
  // Labels come pre-prefixed from get_game_themes (migration 0086):
  // `↪ <forum>` for forums, `# <channel>` for text/announcement. Same
  // convention as the feedback-inbox chips so users see the same
  // shape across surfaces.
  // The right column on the card caps overall width; here we just wrap
  // chip labels to a second row when they don't fit on one line.
  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      {visible.map((c) => {
        const display = truncatePillLabel(c);
        if (display === c) {
          return <Badge key={c} tone="neutral">{c}</Badge>;
        }
        return (
          <Tooltip key={c} content={c}>
            <Badge tone="neutral" className="cursor-default">{display}</Badge>
          </Tooltip>
        );
      })}
      {hidden.length > 0 && (
        <Tooltip
          content={
            <span className="flex flex-col gap-0.5 text-left">
              {hidden.map((c) => (
                <span key={c}>{c}</span>
              ))}
            </span>
          }
        >
          <Badge tone="neutral" className="cursor-default">{`+${hidden.length} more`}</Badge>
        </Tooltip>
      )}
    </div>
  );
}

// Plain-copy error messages keyed off the RPC's typed error token.
// Generic enough not to leak the internal action names back to the user.
function interventionErrorMessage(error: ThemeInterventionError): string {
  if (error === 'not_authenticated') return 'You need to sign in again.';
  if (error === 'not_a_member') return "You don't have permission to do that.";
  if (error === 'not_found') return 'That action is no longer available.';
  if (error === 'invalid_state') return 'That action is no longer available.';
  if (error === 'cross_studio_merge') return 'You can only merge patterns in the same game.';
  return 'Something went wrong. Please try again.';
}

interface MentionRowProps {
  themeId: string;
  aspect: ThemeAspectEvidence;
  studioShortId: string;
  gameShortId: string;
  onRemoved: (aspectId: string) => void;
}

// Build the "View in feed" destination. Three shapes:
//
//   - Playtest mention with both ids ->
//       /s/{studio}/g/{game}/playtests/{playtestId}/sessions/{sessionId}
//       (separate top-level route, not inside /feedback)
//   - Poll mention with pollId ->
//       /s/{studio}/g/{game}/feedback?poll={pollId}
//       Polls don't reuse ?focus because the aspect's feedbackRecordId
//       points at the synth feedback_pool row (the one the AI extracts
//       from), which is a different row from the original Discord
//       poll message that pollGroups bundle. FeedbackInbox matches
//       ?poll against g.head.pollStatus.pollId instead.
//   - Anything else (messages, forms, threads, or playtest/poll
//     mentions whose routing ids didn't make it through an older
//     snapshot) -> /s/{studio}/g/{game}/feedback?focus={feedbackRecordId}
function buildMentionHref(
  aspect: ThemeAspectEvidence,
  studioShortId: string,
  gameShortId: string,
): string {
  const feedbackHrefBase = `/s/${studioShortId}/g/${gameShortId}/feedback`;
  if (
    aspect.feedbackSource === 'playtest'
    && aspect.playtestId !== null
    && aspect.playtestId !== undefined
    && aspect.playtestSessionId !== null
    && aspect.playtestSessionId !== undefined
  ) {
    return `/s/${studioShortId}/g/${gameShortId}/playtests/${aspect.playtestId}/sessions/${aspect.playtestSessionId}`;
  }
  if (
    aspect.feedbackSource === 'poll'
    && aspect.pollId !== null
    && aspect.pollId !== undefined
  ) {
    return `${feedbackHrefBase}?poll=${aspect.pollId}`;
  }
  return `${feedbackHrefBase}?focus=${aspect.feedbackRecordId}`;
}

function MentionRow({
  themeId,
  aspect,
  studioShortId,
  gameShortId,
  onRemoved,
}: MentionRowProps): JSX.Element {
  const { buildMarkers, devStages } = useShell();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const focusHref = buildMentionHref(aspect, studioShortId, gameShortId);
  // Build live when this mention's feedback was posted. Joined client-side
  // from the active game's marker timeline (insights are per-game, so the
  // shell's marker set is correctly scoped). Hidden when the underlying
  // feedback has no timestamp to anchor against.
  const buildMarker =
    aspect.feedbackPostedAt === null
      ? null
      : buildActiveAt(buildMarkers, devStages, aspect.feedbackPostedAt);
  const author = aspect.feedbackAuthorHandle ?? 'anonymous';
  // Server hands us a pre-prefixed label (`↪ <forum>` / `# <channel>`)
  // that already rolls thread messages up to their parent forum, so
  // mentions on a forum thread read as the forum name instead of the
  // thread title. Falls back to the raw name if the server somehow
  // didn't compute one.
  const channel =
    aspect.feedbackChannelLabel
    ?? (aspect.feedbackChannelName !== null ? `# ${aspect.feedbackChannelName}` : null);

  const openConfirm = useCallback(() => {
    setRemoveError(null);
    setConfirmOpen(true);
  }, []);

  const cancelConfirm = useCallback(() => {
    if (removing) return;
    setConfirmOpen(false);
    setRemoveError(null);
  }, [removing]);

  const handleConfirmRemove = useCallback(async () => {
    setRemoving(true);
    setRemoveError(null);
    const result = await detachAspectFromTheme(themeId, aspect.id);
    if (!result.ok) {
      setRemoving(false);
      setRemoveError(interventionErrorMessage(result.error ?? 'unknown'));
      return;
    }
    // Unmount paths: the parent removes this row from its aspects list
    // synchronously via onRemoved, so we don't need to flip local state
    // back; the modal goes away with the row.
    onRemoved(aspect.id);
  }, [themeId, aspect.id, onRemoved]);

  return (
    <li className="rounded-md border border-border bg-surface-3 p-3">
      <blockquote className="text-sm italic text-text">
        “{aspect.textSpan}”
      </blockquote>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-muted">
        <span>{author}</span>
        {channel !== null && (() => {
          // Apply the same truncation as the theme-header channel pills
          // so a long poll question or playtest headline on a mention
          // row doesn't blow out the layout. Full label is still
          // available on hover via the Tooltip.
          const display = truncatePillLabel(channel);
          if (display === channel) {
            return <span>{channel}</span>;
          }
          return (
            <Tooltip content={channel}>
              <span className="cursor-default">{display}</span>
            </Tooltip>
          );
        })()}
        {aspect.feedbackPostedAt !== null && (
          <span>{relativeTime(aspect.feedbackPostedAt)}</span>
        )}
        {aspect.feedbackPostedAt !== null && <BuildMarkerLabel marker={buildMarker} />}
        {aspect.reactionCount > 0 && (
          <span>
            {aspect.reactionCount} reaction
            {aspect.reactionCount === 1 ? '' : 's'}
          </span>
        )}
        <Link
          to={focusHref}
          className="ml-auto text-link underline"
        >
          View in feed
        </Link>
        <button
          type="button"
          onClick={openConfirm}
          className="text-text-muted underline hover:text-text"
        >
          Remove from this pattern
        </button>
      </div>
      <RemoveMentionConfirmModal
        open={confirmOpen}
        aspectId={aspect.id}
        busy={removing}
        error={removeError}
        onConfirm={() => void handleConfirmRemove()}
        onCancel={cancelConfirm}
      />
    </li>
  );
}

interface TrajectoryLineProps {
  bucket: ThemeBucket;
  trajectory: ThemeTrajectory;
}

// One muted line: "Critical now - was Emerging 2 weeks ago". Hidden when
// there is no prior bucket on record, or when the prior bucket matches
// the current one (no transition to surface).
function TrajectoryLine({ bucket, trajectory }: TrajectoryLineProps): JSX.Element | null {
  const { previousBucket, previousClassifiedAt } = trajectory;
  if (previousBucket === null || previousClassifiedAt === null) return null;
  if (previousBucket === bucket) return null;
  return (
    <p className="text-xs text-text-muted">
      {bucketDisplayLabel(bucket)} now — was {bucketDisplayLabel(previousBucket)}{' '}
      {relativeTime(previousClassifiedAt)}
    </p>
  );
}

type ActionsView = 'closed' | 'menu' | 'merge';

export function PatternCard({
  pattern,
  totalMessages,
  studioShortId,
  gameShortId,
  allThemes,
  onChanged,
}: PatternCardProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [aspects, setAspects] = useState<ThemeAspectEvidence[] | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(false);

  const [actionsView, setActionsView] = useState<ActionsView>('closed');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  // Dismiss flows through the shared Modal primitive (not window.confirm)
  // so the confirmation step lives in the portal chrome rather than the
  // browser dialog. The modal owns its own busy + error rendering.
  const [dismissModalOpen, setDismissModalOpen] = useState(false);
  const [dismissError, setDismissError] = useState<string | null>(null);
  const [dismissBusy, setDismissBusy] = useState(false);
  // Restore is the inverse action surfaced only on dismissed cards.
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreBusy, setRestoreBusy] = useState(false);

  const isDismissed = pattern.status === 'dismissed';

  const handleToggle = useCallback(async () => {
    setExpanded((wasExpanded) => !wasExpanded);
    // Lazy fetch on first expand. We don't re-fetch on subsequent
    // toggles - the live Insights page bumps via realtime when a new
    // snapshot lands, which remounts the card via key change anyway.
    if (!expanded && aspects === null && !loadingDetail) {
      setLoadingDetail(true);
      setDetailError(false);
      const detail = await fetchThemeDetail(pattern.id);
      setLoadingDetail(false);
      if (detail === null) {
        setDetailError(true);
        return;
      }
      setAspects(detail.aspects);
    }
  }, [expanded, aspects, loadingDetail, pattern.id]);

  const handleAspectRemoved = useCallback((aspectId: string) => {
    setAspects((current) =>
      current === null ? current : current.filter((a) => a.id !== aspectId),
    );
    onChanged?.();
  }, [onChanged]);

  const closeActions = useCallback(() => {
    setActionsView('closed');
    setActionError(null);
  }, []);

  const openDismissModal = useCallback(() => {
    setActionsView('closed');
    setActionError(null);
    setDismissError(null);
    setDismissModalOpen(true);
  }, []);

  const handleConfirmDismiss = useCallback(async () => {
    setDismissBusy(true);
    setDismissError(null);
    const result = await dismissTheme(pattern.id);
    setDismissBusy(false);
    if (!result.ok) {
      setDismissError(interventionErrorMessage(result.error ?? 'unknown'));
      return;
    }
    setDismissModalOpen(false);
    onChanged?.();
  }, [pattern.id, onChanged]);

  const handleCancelDismiss = useCallback(() => {
    if (dismissBusy) return;
    setDismissModalOpen(false);
    setDismissError(null);
  }, [dismissBusy]);

  const openRestoreModal = useCallback(() => {
    setActionsView('closed');
    setRestoreError(null);
    setRestoreModalOpen(true);
  }, []);

  const handleConfirmRestore = useCallback(async () => {
    setRestoreBusy(true);
    setRestoreError(null);
    const result = await restoreTheme(pattern.id);
    setRestoreBusy(false);
    if (!result.ok) {
      setRestoreError(interventionErrorMessage(result.error ?? 'unknown'));
      return;
    }
    setRestoreModalOpen(false);
    onChanged?.();
  }, [pattern.id, onChanged]);

  const handleCancelRestore = useCallback(() => {
    if (restoreBusy) return;
    setRestoreModalOpen(false);
    setRestoreError(null);
  }, [restoreBusy]);

  const handleMergeSelect = useCallback(async (winnerThemeId: string) => {
    setActionBusy(true);
    setActionError(null);
    const result = await mergeThemes(pattern.id, winnerThemeId);
    setActionBusy(false);
    if (!result.ok) {
      setActionError(interventionErrorMessage(result.error ?? 'unknown'));
      return;
    }
    setActionsView('closed');
    onChanged?.();
  }, [pattern.id, onChanged]);

  const mergeCandidates = allThemes ?? [];
  const canMerge = mergeCandidates.some((t) => t.id !== pattern.id);

  const percentLabel = formatPercent(pattern.memberCount, totalMessages);

  return (
    <article
      className={
        isDismissed
          ? 'rounded-md border border-border bg-surface p-4 opacity-70'
          : 'rounded-md border border-border bg-surface p-4'
      }
    >
      {/*
        Two-column layout:
        - Left: polarity dot, bucket badge, title, axis chip, mention
          stats, trajectory line, the description body, and the channel
          pills. The channel pills live with the description so they
          flow under the prose at narrow widths instead of crowding
          the right column.
        - Right: actions overflow menu and the "Show top mentions"
          button. The button is pinned to the bottom via mt-auto so its
          baseline matches taller cards.
        items-stretch on the parent so the right column matches the
        left column's height even when the description is long.
      */}
      <div className="flex items-stretch gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              aria-label={polarityLabel(pattern.dominantPolarity)}
              title={polarityLabel(pattern.dominantPolarity)}
              className={`inline-block h-2 w-2 shrink-0 rounded-full ${polarityDotClass(pattern.dominantPolarity)}`}
            />
            {pattern.bucket !== 'none' && (
              <Badge tone={bucketBadgeTone(pattern.bucket)}>
                {bucketDisplayLabel(pattern.bucket)}
              </Badge>
            )}
            <h3 className="text-sm font-semibold text-text">{pattern.title}</h3>
            <Badge tone="neutral">
              {contentAxisLabel(pattern.contentAxis)}
            </Badge>
          </div>
          <p className="text-xs text-text-muted">
            {pattern.memberCount} mention{pattern.memberCount === 1 ? '' : 's'}
            {' · '}
            {percentLabel} of messages
            {' · '}
            first seen {relativeTime(pattern.earliestMentionAt ?? pattern.firstSeenAt)}
          </p>
          <TrajectoryLine
            bucket={pattern.bucket}
            trajectory={pattern.trajectory}
          />
          {pattern.description !== null && (
            <p
              className={
                expanded
                  ? 'mt-1 whitespace-pre-wrap text-sm text-text-muted'
                  : 'mt-1 line-clamp-3 text-sm text-text-muted'
              }
            >
              {pattern.description}
            </p>
          )}
          {pattern.channelNames.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1">
              <ChannelPills channels={pattern.channelNames} />
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <Popover
            open={actionsView !== 'closed'}
            onOpenChange={(open) => {
              if (!open) closeActions();
              else setActionsView('menu');
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Pattern actions"
                className="-mt-1 rounded-sm px-1 text-text-muted hover:bg-surface-3 hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                ⋯
              </button>
            </PopoverTrigger>
            {/*
              Render the menu view by default. The merge picker only
              shows up while actionsView === 'merge', so the brief
              close-animation frame (actionsView === 'closed') still
              paints the menu view, matching what the user had open.
            */}
            <PopoverContent align="end" className="p-2">
              {isDismissed ? (
                // Dismissed cards have a single Restore action. Merge is
                // intentionally hidden so merging into a dismissed source
                // doesn't fight the "dismissed catches mentions silently"
                // model.
                <div className="flex w-48 flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={openRestoreModal}
                    className="rounded-sm px-2 py-1.5 text-left text-sm text-text hover:bg-surface-3"
                  >
                    Restore
                  </button>
                </div>
              ) : actionsView === 'merge' ? (
                <MergeThemePicker
                  candidates={mergeCandidates}
                  excludeId={pattern.id}
                  onSelect={(winnerId) => void handleMergeSelect(winnerId)}
                  onCancel={() => setActionsView('menu')}
                  errorMessage={actionError}
                  busy={actionBusy}
                />
              ) : (
                <div className="flex w-48 flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={openDismissModal}
                    className="rounded-sm px-2 py-1.5 text-left text-sm text-text hover:bg-surface-3"
                  >
                    Dismiss
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActionError(null);
                      setActionsView('merge');
                    }}
                    disabled={!canMerge}
                    className="rounded-sm px-2 py-1.5 text-left text-sm text-text hover:bg-surface-3 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Merge into…
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <Button
            variant="ghost"
            size="sm"
            className="mt-auto"
            onClick={() => void handleToggle()}
            aria-expanded={expanded}
          >
            {expanded ? 'Hide top mentions' : 'Show top mentions'}
          </Button>
        </div>
      </div>

      <DismissPatternConfirmModal
        open={dismissModalOpen}
        patternTitle={pattern.title}
        patternId={pattern.id}
        busy={dismissBusy}
        error={dismissError}
        onConfirm={() => void handleConfirmDismiss()}
        onCancel={handleCancelDismiss}
      />

      <RestorePatternConfirmModal
        open={restoreModalOpen}
        patternTitle={pattern.title}
        patternId={pattern.id}
        busy={restoreBusy}
        error={restoreError}
        onConfirm={() => void handleConfirmRestore()}
        onCancel={handleCancelRestore}
      />

      <Collapsible open={expanded}>
        <section aria-label="Top mentions" className="space-y-2 pt-3">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            Top mentions
          </h4>
          {loadingDetail && (
            <p className="text-sm text-text-muted">Loading mentions…</p>
          )}
          {detailError && (
            <p className="text-sm text-danger-text">
              Couldn&apos;t load mentions for this pattern.
            </p>
          )}
          {aspects !== null && aspects.length === 0 && (
            <p className="text-sm text-text-muted">No mentions to show yet.</p>
          )}
          {aspects !== null && aspects.length > 0 && (
            <ul className="space-y-2">
              {aspects.map((a) => (
                <MentionRow
                  key={a.id}
                  themeId={pattern.id}
                  aspect={a}
                  studioShortId={studioShortId}
                  gameShortId={gameShortId}
                  onRemoved={handleAspectRemoved}
                />
              ))}
            </ul>
          )}
        </section>
      </Collapsible>
    </article>
  );
}
